import { FC } from 'react';
import { Navigate, useNavigate, useRouteError } from 'react-router-dom';
import { AppProvider, DashboardLayout, Router } from '@toolpad/core';
import { Container, Typography, Button } from '@mui/material';
import { captureException } from '@sentry/react';

import { AuthRole } from '@libs/types';
import { theme } from '@carcareers/theme';
import { useAuth } from '@carcareers/hooks';
import { Logo, ToolbarActions, Footer } from '@carcareers/components';
import { routes, navigation } from '@carcareers/utils';


export const DashboardErrorBoundary: FC = () => {
  const auth = useAuth();
  const { user } = useAuth();
  const navigate = useNavigate();
  const error = useRouteError();

  captureException(error, {
    contexts: {
      errorBoundary: {
        componentStack: 'DashboardErrorBoundary',
        errorBoundary: true,
      },
    },
  });

  const redirects: Record<'employer' | 'admin', string> = {
    employer: `/employer`,
    admin: `/admin`,
  };
  const redirect = redirects[user?.role as 'employer' | 'admin'] || redirects['employer'];

  const role = auth.session.user?.role as AuthRole;
  if (!role) {
    return <Navigate to={routes.HOME} />;
  }

  const nav = navigation[role];
  const branding = {
    logo: <Logo role={role} />,
    title: '',
  };

  const router: Router = {
    pathname: window.location.pathname,
    searchParams: new URLSearchParams(window.location.search),
    navigate: (url) => {
      window.location.href = url.toString();
    },
  };

  return (
    <AppProvider branding={branding} router={router} navigation={nav} theme={theme}>
      <DashboardLayout slots={{ toolbarActions: ToolbarActions }}>
        <Container
          maxWidth="md"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'center',
            minHeight: { xs: 'calc(100vh - 160px)', sm: 'calc(100vh - 132px)' },
            gap: 4,
          }}
        >
          <Typography variant="h1">500</Typography>
          <Typography variant="h2">Woah! That took a wrong turn.</Typography>
          <Typography variant="body1">Please reroute to the home page.</Typography>
          <Button
            variant="contained"
            onClick={() => navigate(redirect)}
            sx={{
              backgroundColor: 'warning.light',
              color: 'warning.contrastText',
              '&:hover': {
                backgroundColor: 'warning.contrastText',
                color: 'background.default',
              },
            }}
          >
            Go Home
          </Button>
        </Container>
        <Footer />
      </DashboardLayout>
    </AppProvider>
  );
};

export default DashboardErrorBoundary;
